import { defineStore } from "pinia";
import type { FacilityFormData, FacilityUpsertPayload } from "@/types/wizard";
import type { ProductCategoryDto } from "@ckweb/ckfetch/connectables/setup";

export const useSetupWizardStore = defineStore("setupWizard", () => {
  const skipIntroduction = useLocalStorage<boolean>(
    "setup.skipIntroduction",
    false,
  );

  const formData = reactive<{
    facility?: FacilityFormData;
  }>({});

  const unsavedChangesState = reactive({
    hasUnsavedChanges: false,
    showLeaveConfirmation: false,
    confirmationLeaveRoute: undefined as string | undefined,
  });

  const {
    $i18n: { t },
  } = useNuxtApp();

  const createFacility = useCreateFacility();
  const updateFacility = useUpdateFacility();
  const getCleaningRoles = useGetCleaningRoleOptions();
  const getProductCategories = useGetProductCategories();

  const showLeaveConfirmationDialog = computed(
    () => unsavedChangesState.showLeaveConfirmation,
  );

  const facilityFormData = computed(
    () => formData.facility ?? ({} as FacilityUpsertPayload),
  );

  const productCategoryOptions = computed<
    ValueLabelPair<Omit<ProductCategoryDto, "id"> & { id?: number }>[]
  >(
    () =>
      getProductCategories.data.value?.map((defItem) => {
        const existing = facilityFormData.value.productCategories?.find(
          (item) => item.description === defItem.description,
        );
        return {
          value: existing ?? { description: defItem.description },
          label: defItem.description,
        };
      }) ?? [],
  );

  const confirmUnsavedChangesBeforeLeaving = (toRoute: string) => {
    if (!unsavedChangesState.hasUnsavedChanges) {
      unsavedChangesState.showLeaveConfirmation = false;
      unsavedChangesState.confirmationLeaveRoute = undefined;
      return false;
    }

    unsavedChangesState.showLeaveConfirmation = true;
    unsavedChangesState.confirmationLeaveRoute = toRoute;
    return true;
  };

  async function init(currentFacility?: FacilityDto) {
    const { organization, ...rest } = currentFacility ?? {
      organization: undefined,
    };

    formData.facility = rest as FacilityFormData;
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  }

  function reset() {
    formData.facility = undefined;
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;
    unsavedChangesState.confirmationLeaveRoute = undefined;
  }

  const getCleaningRoleOptions = async (category: CleaningRoleCategory) =>
    getCleaningRoles.safeFetch(category, {
      fallback: (data) =>
        data?.map((role) => ({
          value: role,
          label: role.name,
        })) ?? [],
    });

  function formDataChanged(facilityUpdate: FacilityUpsertPayload) {
    if (!formData.facility) return;
    const upsertFacilityPayload = normalizeFacilityPayload({
      ...formData.facility,
      ...facilityUpdate,
    });

    if (!formData.facility?.id) {
      unsavedChangesState.hasUnsavedChanges = true;
      return upsertFacilityPayload;
    }

    const currentFacilityPayload = normalizeFacilityPayload(formData.facility);

    const hasFacilityChanges = !$ck._.isEqual(
      upsertFacilityPayload,
      currentFacilityPayload,
    );

    unsavedChangesState.hasUnsavedChanges = hasFacilityChanges;

    return hasFacilityChanges ? upsertFacilityPayload : undefined;
  }

  async function saveFacility(facilityUpdate: FacilityUpsertPayload) {
    const upsertFacilityPayload = formDataChanged(facilityUpdate);

    if (!unsavedChangesState.hasUnsavedChanges || !upsertFacilityPayload) {
      unsavedChangesState.hasUnsavedChanges = false;
      return true;
    }

    const payload = {
      ...facilityDtoDefaults(),
      ...upsertFacilityPayload,
    } as FacilityDto;

    const responsePromise = payload.id
      ? updateFacility.safeMutate(payload.id, payload)
      : createFacility.safeMutate(payload);

    const facility = await responsePromise;
    if (!facility) return false;
    formData.facility = facility;
    unsavedChangesState.hasUnsavedChanges = false;

    return true;
  }

  function cancelLeave() {
    unsavedChangesState.confirmationLeaveRoute = undefined;
    unsavedChangesState.showLeaveConfirmation = false;
  }

  function confirmLeave() {
    unsavedChangesState.hasUnsavedChanges = false;
    unsavedChangesState.showLeaveConfirmation = false;

    if (unsavedChangesState.confirmationLeaveRoute) {
      navigateTo(unsavedChangesState.confirmationLeaveRoute);
      unsavedChangesState.confirmationLeaveRoute = undefined;
    }
  }

  return {
    formData,
    facilityFormData,
    productCategoryOptions,
    skipIntroduction,
    unsavedChangesState,
    showLeaveConfirmationDialog,
    confirmUnsavedChangesBeforeLeaving,
    init,
    reset,
    // TODO: we need the apis for rooms/layouts options
    getProductCategories: getProductCategories.safeFetch,
    getFreeStandingBuildingOptions: () => mockFreestandingBuildingsOptions(t),
    getRoomsAndLayoutsOptions: () => mockRoomsAndLayoutsOptions(t),
    getCleaningRoleOptions,
    formDataChanged,
    saveFacility,
    cancelLeave,
    confirmLeave,
  };
});
